import React, { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { Spinner } from '@easydent/shared-ui';
import { type DocPage, type DocsHit, getDoc, searchDocs } from '@/lib/api/docs';
import { docsUrl, helpSlugForPath } from '@/lib/helpLinks';
import { useHelpStore } from '@/stores/helpStore';

/**
 * The guides, read next to the screen they explain.
 *
 * <p>
 * Help used to open the documentation site in a new tab. That is fine for browsing and wrong for
 * doing: a receptionist halfway through a booking loses the screen they were asking about, and has
 * to hold the instructions in their head on the way back. Rendering the guide in a drawer keeps both
 * on screen at once, which is the entire point of contextual help.
 * </p>
 *
 * <p>
 * The markdown comes from the API rather than the help site — the site is a different origin and
 * compiles its content into its own bundle, so it cannot be fetched from here. The API ships the
 * same files, so there is still one copy.
 * </p>
 */
const HelpDrawer: React.FC = () => {
  const { t, i18n } = useTranslation('common');
  const { pathname, search: locationSearch } = useLocation();
  const { open, slug, close, show } = useHelpStore();

  const [page, setPage] = useState<DocPage | null>(null);
  const [hits, setHits] = useState<DocsHit[] | null>(null);
  const [query, setQuery] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);

  // Escape closes it, like every other drawer in the app.
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && close();
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, close]);

  useEffect(() => {
    if (!open || !slug) {
      setPage(null);
      return;
    }
    setLoading(true);
    setError(false);
    getDoc(slug)
      .then(setPage)
      .catch(() => setError(true))
      .finally(() => setLoading(false));
  }, [open, slug]);

  useEffect(() => {
    if (!open || query.trim().length < 2) {
      setHits(null);
      return;
    }
    const timer = setTimeout(() => {
      searchDocs(query.trim()).then((r) => setHits(r.hits)).catch(() => setHits([]));
    }, 250);
    return () => clearTimeout(timer);
  }, [open, query]);

  if (!open) return null;

  const currentSlug = helpSlugForPath(pathname, locationSearch);

  return (
    <>
      <div className="fixed inset-0 z-40 bg-black/20" onClick={close} aria-hidden="true" />
      <aside
        role="dialog"
        aria-modal="true"
        aria-label={t('help.title', 'Help')}
        className="fixed top-0 right-0 z-50 flex h-full w-full max-w-md flex-col border-l border-line bg-surface shadow-xl"
      >
        <header className="flex shrink-0 items-center gap-2 border-b border-line px-4 py-3">
          {slug && (
            <button
              onClick={() => show(null)}
              className="rounded p-1 text-muted hover:bg-subtle hover:text-body-strong"
              aria-label={t('help.back', 'Back to all guides')}
            >
              ←
            </button>
          )}
          <h2 className="flex-1 truncate font-semibold text-body">
            {slug && page ? page.title : t('help.title', 'Help')}
          </h2>
          <button
            onClick={close}
            className="rounded p-1 text-muted hover:bg-subtle hover:text-body-strong"
            aria-label={t('close', 'Close')}
          >
            ✕
          </button>
        </header>

        <div className="shrink-0 border-b border-line-soft px-4 py-2.5">
          <input
            type="search"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder={t('help.search', 'Search the guides…')}
            className="w-full rounded-lg border border-line-strong px-3 py-1.5 text-sm focus:border-brand-500 focus:outline-none"
          />
        </div>

        <div className="min-h-0 flex-1 overflow-y-auto px-4 py-4">
          {hits !== null ? (
            hits.length === 0 ? (
              <p className="py-6 text-center text-sm text-muted">
                {t('help.noResults', 'Nothing matches that. Try different words.')}
              </p>
            ) : (
              <ul className="space-y-2">
                {hits.map((hit) => (
                  <li key={`${hit.slug}-${hit.heading}`}>
                    <button
                      onClick={() => { setQuery(''); show(hit.slug); }}
                      className="w-full rounded-lg border border-line p-3 text-left transition hover:border-brand-300 hover:bg-brand-50"
                    >
                      <span className="block text-sm font-medium text-body">{hit.title}</span>
                      <span className="block text-xs text-muted">{hit.heading}</span>
                      <span className="mt-1 block text-xs text-muted-strong">{hit.excerpt}</span>
                    </button>
                  </li>
                ))}
              </ul>
            )
          ) : loading ? (
            <div className="flex justify-center py-10"><Spinner /></div>
          ) : error ? (
            <p className="text-sm text-muted-strong">{t('help.loadFailed', 'That guide could not be loaded.')}</p>
          ) : page ? (
            <article>
              {page.isFallback && (
                <p className="mb-4 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-900">
                  {t('help.inEnglish', 'This guide is not translated yet, so it is shown in English.')}
                </p>
              )}
              <ReactMarkdown
                remarkPlugins={[remarkGfm]}
                components={{
                  h1: (p) => <h1 className="mb-2 text-xl font-semibold text-body" {...p} />,
                  h2: (p) => <h2 className="mt-6 mb-2 border-b border-line-soft pb-1 text-base font-semibold text-body" {...p} />,
                  h3: (p) => <h3 className="mt-4 mb-1 text-sm font-semibold text-body" {...p} />,
                  p: (p) => <p className="my-2 text-sm leading-6 text-body-soft" {...p} />,
                  ul: (p) => <ul className="my-2 list-disc space-y-1 pl-5 text-sm text-body-soft" {...p} />,
                  ol: (p) => <ol className="my-2 list-decimal space-y-1 pl-5 text-sm text-body-soft" {...p} />,
                  strong: (p) => <strong className="font-semibold text-body" {...p} />,
                  blockquote: (p) => (
                    <blockquote className="my-3 rounded-r border-l-4 border-brand-300 bg-brand-50 px-3 py-2 text-sm text-body-soft" {...p} />
                  ),
                  code: (p) => <code className="rounded bg-subtle px-1 py-0.5 text-xs text-body-strong" {...p} />,
                  table: (p) => <div className="my-3 overflow-x-auto"><table className="w-full border-collapse text-left text-xs" {...p} /></div>,
                  th: (p) => <th className="border-b border-line-strong px-2 py-1 font-semibold text-body" {...p} />,
                  td: (p) => <td className="border-b border-line-soft px-2 py-1 text-body-soft" {...p} />,
                  // In-app links stay in the drawer; anything else behaves like a normal link.
                  a: ({ href, children, ...rest }) => {
                    const internal = href?.startsWith('/') && !href.startsWith('//');
                    return internal ? (
                      <button
                        type="button"
                        onClick={() => show(href!.replace(/^\//, ''))}
                        className="text-brand-ink underline hover:text-brand-ink-strong"
                      >
                        {children}
                      </button>
                    ) : (
                      <a href={href} target="_blank" rel="noopener noreferrer" className="text-brand-ink underline" {...rest}>
                        {children}
                      </a>
                    );
                  },
                }}
              >
                {page.body}
              </ReactMarkdown>
            </article>
          ) : (
            <div className="space-y-3">
              <p className="text-sm text-muted-strong">
                {t('help.intro', 'Search above, or start with the guide for this screen.')}
              </p>
              {currentSlug && (
                <button
                  onClick={() => show(currentSlug)}
                  className="w-full rounded-lg border border-brand-200 bg-brand-50 p-3 text-left text-sm font-medium text-brand-ink-deep hover:bg-brand-100"
                >
                  {t('help.forThisPage', 'Help for this page')} →
                </button>
              )}
            </div>
          )}
        </div>

        <footer className="shrink-0 border-t border-line-soft px-4 py-2.5">
          <a
            href={docsUrl(slug ?? currentSlug, i18n.language)}
            target="_blank"
            rel="noopener noreferrer"
            className="text-xs text-muted hover:text-brand-ink-strong hover:underline"
          >
            {t('help.openSite', 'Open the full help site')} ↗
          </a>
        </footer>
      </aside>
    </>
  );
};

export default HelpDrawer;
